import { apiClient } from './client'
import type {
  BindBrokerAnswerDto,
  BindBrokerAnswerResultDto,
  BrokerLineOptionDto,
  BrokerRequestDetailDto,
  BrokerRequestsQuery,
  BrokerRequestsResponse,
  BrokerUploadResultDto,
  CreateBrokerRequestDto,
  CustomsAnswerDto,
  CustomsAnswerPatchDto,
  CustomsBrokerDto,
  CustomsBrokerInput,
  CustomsBulkResult,
  CustomsQueueOptions,
  CustomsQueueQuery,
  CustomsQueueResponse,
  CustomsQueueRow,
  CustomsSendReport,
  CustomsSendToLogistResult,
  CustomsMissedMarkResult,
  CustomsStatusDto,
  UpdateCustomsBrokerDto,
  UploadBrokerAnswerDto,
} from '@crm/shared'

/**
 * Нетарифка (логист, Л1–Л3, docs/features/nontariff.md). Смотрят логист, ADMIN,
 * гендиректор и РОЗ; правит только логист — остальным запись вернёт 403.
 */

type Envelope<T> = { success: true; data: T }

export interface CustomsMentionableUser {
  id: string
  name: string | null
  email: string
  role: string
}

// ─── Очередь ────────────────────────────────────────────────────────────────

export async function getCustomsQueue(
  query: CustomsQueueQuery = {},
): Promise<CustomsQueueResponse> {
  const res = await apiClient.get<Envelope<CustomsQueueResponse>>('/customs/queue', {
    params: query,
  })
  return res.data.data
}

export async function getCustomsQueueOptions(): Promise<CustomsQueueOptions> {
  const res = await apiClient.get<Envelope<CustomsQueueOptions>>('/customs/queue/options')
  return res.data.data
}

/** Одна позиция очереди — для перехода из уведомления и шапки панели комментариев. */
export async function getCustomsItem(itemId: string): Promise<CustomsQueueRow> {
  const res = await apiClient.get<Envelope<CustomsQueueRow>>(`/customs/items/${itemId}`)
  return res.data.data
}

/** Кого логист может @упомянуть в комментарии к позиции (менеджер, помощники, закупка…). */
export async function getCustomsMentionableUsers(
  itemId: string,
): Promise<CustomsMentionableUser[]> {
  const res = await apiClient.get<Envelope<CustomsMentionableUser[]>>(
    `/customs/items/${itemId}/mentionable-users`,
  )
  return res.data.data
}

/** Ручной ввод ответа брокера — сразу на несколько позиций. */
export async function saveCustomsAnswer(dto: CustomsAnswerDto): Promise<CustomsBulkResult> {
  const res = await apiClient.post<Envelope<CustomsBulkResult>>('/customs/answers', dto)
  return res.data.data
}

/** Правка одной ячейки ответа в очереди; сервер отдаёт обновлённую строку очереди. */
export async function patchCustomsAnswer(
  itemId: string,
  patch: CustomsAnswerPatchDto,
): Promise<CustomsQueueRow> {
  const res = await apiClient.patch<Envelope<CustomsQueueRow>>(
    `/customs/items/${itemId}/answer`,
    patch,
  )
  return res.data.data
}

/** Ручная смена статуса: «В очереди» / «Нужна информация» / «Не требуется». */
export async function setCustomsStatus(dto: CustomsStatusDto): Promise<CustomsBulkResult> {
  const res = await apiClient.post<Envelope<CustomsBulkResult>>('/customs/status', dto)
  return res.data.data
}

/** «Оставить прошлый ответ»: ответ из памяти по партномеру, без нового запроса брокеру. */
export async function acceptPreviousCustomsAnswers(itemIds: string[]): Promise<CustomsBulkResult> {
  const res = await apiClient.post<Envelope<CustomsBulkResult>>('/customs/answers/previous', {
    itemIds,
  })
  return res.data.data
}

export async function setCustomsDatasheet(
  itemId: string,
  datasheetUrl: string | null,
): Promise<{ datasheetUrl: string | null }> {
  const res = await apiClient.patch<Envelope<{ datasheetUrl: string | null }>>(
    `/customs/items/${itemId}/datasheet`,
    { datasheetUrl },
  )
  return res.data.data
}

// ─── Запросы брокеру ────────────────────────────────────────────────────────

/** Создаёт запрос брокеру: позиции → «Отправлено брокеру», файл собирает сайт. */
export async function createBrokerRequest(
  dto: CreateBrokerRequestDto,
): Promise<BrokerRequestDetailDto> {
  const res = await apiClient.post<Envelope<BrokerRequestDetailDto>>(
    '/customs/broker-requests',
    dto,
  )
  return res.data.data
}

/** Ответ брокера, уже разобранный из Excel. */
/** Строки запросов брокеру по части партномера — для ручной привязки артикула из файла. */
export async function searchBrokerLines(q: string): Promise<BrokerLineOptionDto[]> {
  const res = await apiClient.get<Envelope<BrokerLineOptionDto[]>>(
    '/customs/broker-requests/lines',
    { params: { q } },
  )
  return res.data.data
}

export async function bindBrokerAnswer(
  dto: BindBrokerAnswerDto,
): Promise<BindBrokerAnswerResultDto> {
  const res = await apiClient.post<Envelope<BindBrokerAnswerResultDto>>(
    '/customs/broker-requests/bind',
    dto,
  )
  return res.data.data
}

export async function uploadBrokerAnswer(
  dto: UploadBrokerAnswerDto,
): Promise<BrokerUploadResultDto> {
  const res = await apiClient.post<Envelope<BrokerUploadResultDto>>(
    '/customs/broker-requests/upload',
    dto,
  )
  return res.data.data
}

export async function getBrokerRequests(
  query: BrokerRequestsQuery = {},
): Promise<BrokerRequestsResponse> {
  const res = await apiClient.get<Envelope<BrokerRequestsResponse>>('/customs/broker-requests', {
    params: query,
  })
  return res.data.data
}

export async function getBrokerRequest(id: string): Promise<BrokerRequestDetailDto> {
  const res = await apiClient.get<Envelope<BrokerRequestDetailDto>>(
    `/customs/broker-requests/${id}`,
  )
  return res.data.data
}

/** Отменить запрос: позиции «Отправлено брокеру» возвращаются «В очередь». */
export async function deleteBrokerRequest(id: string): Promise<{ reverted: number }> {
  const res = await apiClient.delete<Envelope<{ reverted: number }>>(
    `/customs/broker-requests/${id}`,
  )
  return res.data.data
}

/** Указать или сменить брокера у запроса; null — «не указан». */
export async function setBrokerRequestBroker(
  id: string,
  brokerId: string | null,
): Promise<BrokerRequestDetailDto> {
  const res = await apiClient.patch<Envelope<BrokerRequestDetailDto>>(
    `/customs/broker-requests/${id}/broker`,
    { brokerId },
  )
  return res.data.data
}

// ─── Справочник брокеров ────────────────────────────────────────────────────

export async function getCustomsBrokers(includeHidden = false): Promise<CustomsBrokerDto[]> {
  const res = await apiClient.get<Envelope<CustomsBrokerDto[]>>('/customs/brokers', {
    params: includeHidden ? { includeHidden: '1' } : {},
  })
  return res.data.data
}

export async function createCustomsBroker(dto: CustomsBrokerInput): Promise<CustomsBrokerDto> {
  const res = await apiClient.post<Envelope<CustomsBrokerDto>>('/customs/brokers', dto)
  return res.data.data
}

export async function updateCustomsBroker(
  id: string,
  dto: UpdateCustomsBrokerDto,
): Promise<CustomsBrokerDto> {
  const res = await apiClient.patch<Envelope<CustomsBrokerDto>>(`/customs/brokers/${id}`, dto)
  return res.data.data
}

// ─── Отправка логисту и отчёт (17.10.2026) ─────────────────────────────────

/** «Отправить брокеру» в карточке заявки: позиции уходят логисту в очередь. */
export async function sendItemsToLogist(itemIds: string[]): Promise<CustomsSendToLogistResult> {
  const res = await apiClient.post<Envelope<CustomsSendToLogistResult>>('/customs/send-to-logist', {
    itemIds,
  })
  return res.data.data
}

/** Отчёт «Нетарифка» в «Аналитике»: что менеджеры отправляли логисту. */
export async function getCustomsSendReport(params: {
  period?: string
  date?: string | null
  from?: string | null
  to?: string | null
  search?: string
}): Promise<CustomsSendReport> {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
  const res = await apiClient.get<Envelope<CustomsSendReport>>('/customs/send-report', {
    params: clean,
  })
  return res.data.data
}

/** Отчёт «Нетарифка»: поставить или снять «Нужна была отправка» у неотправленных позиций. */
export async function markCustomsMissed(
  itemIds: string[],
  missed: boolean,
): Promise<CustomsMissedMarkResult> {
  const res = await apiClient.post<Envelope<CustomsMissedMarkResult>>(
    '/customs/send-report/missed',
    { itemIds, missed },
  )
  return res.data.data
}
